import React, { useState, useEffect } from 'react';
import { GameProvider, useGame } from './context/GameContext';
import NavBar from './components/NavBar';
import Dashboard from './components/Dashboard';
import Lessons from './components/Lessons';
import LessonDetail from './components/LessonDetail';
import Quiz from './components/Quiz';
import QuizResult from './components/QuizResult';
import Achievements from './components/Achievements';
import Leaderboard from './components/Leaderboard';
import PlayerSetup from './components/PlayerSetup';
import Notification from './components/Notification';
import LandingPage from './components/LandingPage';
import K12Preview from './components/K12Preview';
import AdultsPreview from './components/AdultsPreview';
import './App.css';

const HASH_SCREENS = ['landing', 'adults', 'k12'];

function parseHash() {
  const raw = window.location.hash.replace('#', '');
  const [screen, subRoute = null] = raw.split('/');
  return { screen: HASH_SCREENS.includes(screen) ? screen : 'landing', subRoute };
}

function AppInner() {
  const { username, notification } = useGame();
  const [page, setPage] = useState('dashboard');
  const [activeTopic, setActiveTopic] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [appScreen, setAppScreen] = useState(() => parseHash().screen);
  // Capture sub-route once on mount for deep-link support (e.g. #adults/course1)
  const initialSubRoute = React.useRef(parseHash().subRoute);

  const go = (screen, subRoute = null) => {
    const hash = screen === 'landing' ? '' : subRoute ? `${screen}/${subRoute}` : screen;
    window.location.hash = hash;
    setAppScreen(screen);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const onHashChange = () => {
      const { screen } = parseHash();
      setAppScreen(prev => {
        if (prev !== screen) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return screen;
        }
        return prev;
      });
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (appScreen === 'landing') {
    return <LandingPage onSelectK12={() => go('k12')} onSelectAdults={() => go('adults')} />;
  }
  if (appScreen === 'adults') {
    return <AdultsPreview onBack={() => go('landing')} onSelectK12={() => go('k12')} initialPreview={initialSubRoute.current} />;
  }
  if (appScreen === 'k12') {
    return <K12Preview onBack={() => go('landing')} initialCamp={initialSubRoute.current} />;
  }

  if (!username) {
    return <PlayerSetup />;
  }

  const navigate = (target, topic = null) => {
    setActiveTopic(topic);
    if (target !== 'quiz-result') setQuizResult(null);
    setPage(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuizComplete = (result) => {
    setQuizResult(result);
    setPage('quiz-result');
  };

  const renderPage = () => {
    switch (page) {
      case 'dashboard':
        return <Dashboard navigate={navigate} />;
      case 'lessons':
        return <Lessons navigate={navigate} />;
      case 'lesson-detail':
        return <LessonDetail topic={activeTopic} navigate={navigate} />;
      case 'quiz':
        return <Quiz topic={activeTopic} navigate={navigate} onComplete={handleQuizComplete} />;
      case 'quiz-result':
        return <QuizResult result={quizResult} topic={activeTopic} navigate={navigate} />;
      case 'achievements':
        return <Achievements />;
      case 'leaderboard':
        return <Leaderboard />;
      default:
        return <Dashboard navigate={navigate} />;
    }
  };

  return (
    <div className="app">
      <NavBar page={page} navigate={navigate} />
      <main className="app-main">
        <div className="page-container animate-in" key={page}>
          {renderPage()}
        </div>
      </main>
      {notification && <Notification notification={notification} />}
    </div>
  );
}

export default function App() {
  return (
    <GameProvider>
      <AppInner />
    </GameProvider>
  );
}
